// ABOUTME: SPEC-52 D-13 — authored geometry for the waitlist modal's fields/chips, layered onto
// ABOUTME: the consent/privacy panel's own frosted-container tokens (railGeometry/mobileGeometry).

import { CONSENT_PANEL, CONSENT_TEXT } from '../ReelChrome/railGeometry';
import { MOBILE_CONSENT, MOBILE_CONSENT_TEXT } from '../ReelChrome/mobileGeometry';

// OQ-1, decided (owner, 2026-09-10): the confirmed state holds for exactly this long before the
// panel closes itself. Named here — never a literal in useWaitlist.js/WaitlistModal.js.
export const CONFIRM_DWELL_MS = 1500;

// VER-89: how long a submit may sit unanswered before the panel says so. The submit travels on
// the agent WebSocket and is answered by the backend's `waitlist_form {state:'confirmed'}`, which
// is dispatched only AFTER the join is recorded — so this has to allow for a slow turn, not just
// a round trip. Long enough not to cry wolf on a healthy-but-busy connection; short enough that a
// guest on a dead socket is not left reading "Sending…" indefinitely.
export const SUBMIT_TIMEOUT_MS = 12000;

// Review finding, 2026-09-10: the carousel pill (CollectionOverlay.js), the at-rest product card
// (ReelProductCard.js) and the wishlist/cart row (App.js's priceDisplay) each had their OWN
// `price.display || price_info.price` fallback — a price string that reads empty once the backend
// blanks it (R6/D-4), but nothing on any of those three surfaces checked the `waitlist` flag
// itself, so an unblanked or partially-cached record could still leak a figure. One shared label,
// swapped in wherever `item.waitlist` is true, INSTEAD OF reading price at all — never a
// per-surface literal, so the wording cannot drift across the four places it appears.
export const WAITLIST_PRICE_LABEL = 'Waitlist';

// One input row, authored against the consent panel's own row unit (CONSENT_PANEL.rowH/rowGap) so
// a field reads as one more row in the same panel language rather than an invented control. No
// Figma frame exists for this panel (OQ-6, decided: build to the existing consent/privacy frames).
// `label`/`input`/`error` are pinned to their composition's own `rowBody` token with NO shrink
// factor (review finding HIGH 4, 2026-09-10): WaitlistModal.js's `type()` floor is
// `MIN_TEXT_PX * authored / text.rowBody`, so an authored size below `rowBody` resolves a floor
// BELOW `MIN_TEXT_PX` — a real sub-11px legibility bug the mobile chip/error tokens shipped with
// (0.85x on `MOBILE_CONSENT_TEXT.cta`, which equals `rowBody` on mobile, floored at 9.35px) until
// a corrected `responsiveMatrix.test.js` assertion caught it. Keep every token here >= rowBody.
export const FIELD = {
  h: CONSENT_PANEL.rowH * 0.46,
  radius: CONSENT_PANEL.rowRadius,
  gap: CONSENT_PANEL.rowGap * 0.5,
  padX: 24,
  label: CONSENT_TEXT.rowBody,
  input: CONSENT_TEXT.rowBody,
  error: CONSENT_TEXT.rowBody,
};

export const MOBILE_FIELD = {
  h: MOBILE_CONSENT.rowH * 0.46,
  radius: MOBILE_CONSENT.rowRadius,
  gap: MOBILE_CONSENT.rowGap * 0.5,
  padX: 20,
  label: MOBILE_CONSENT_TEXT.rowBody,
  input: MOBILE_CONSENT_TEXT.rowBody,
  error: MOBILE_CONSENT_TEXT.rowBody,
};

// A variant chip — a small pill sized off the panel's own CTA token, so it reads as a control
// (tap to toggle) rather than a static tag. `cta >= rowBody` on both compositions (equal on
// mobile), so using it directly — instead of a sub-1 multiplier — keeps the token above the
// scaled floor above.
export const CHIP = {
  h: CONSENT_PANEL.footerH * 0.6,
  radius: CONSENT_PANEL.rowRadius,
  padX: 24,
  gap: 16,
  font: CONSENT_TEXT.cta,
};

export const MOBILE_CHIP = {
  h: MOBILE_CONSENT.footerH * 0.6,
  radius: MOBILE_CONSENT.rowRadius,
  padX: 20,
  gap: 12,
  font: MOBILE_CONSENT_TEXT.cta,
};

// D-13: the close X's hit box, verbatim from `PrivacyPolicy.js:21-24` (not exported there, so
// re-declared here rather than reached into) — same 44px comfortable-hit-area reasoning.
export const CLOSE_BOX_AUTHORED = 88;
export const CLOSE_BOX_MIN_PX = 44;
export const CLOSE_GLYPH_AUTHORED = 40;
export const CLOSE_GLYPH_MIN_PX = 20;
